import { Box } from '@mui/material';
import type { ReactElement } from 'react';

import { defaultFormat, formatMaybe, type ValueFormatter } from './alternative';
import { ChartFrame } from './ChartFrame';
import { isValue, type Maybe } from './geometry';
import { Legend } from './Legend';
import { seriesTokenAt, useTokens, type SeriesToken } from './seriesPalette';
import { displayValue, type DisplayableValue } from '../kit/displayValue';

/**
 * Anneau avec legende (maquette « Répartition » : anneau, total au centre,
 * legende avec valeurs).
 *
 * - **Le total du centre est fourni** par l'appelant (`total`) et affiche tel
 *   quel : l'anneau ne somme ni n'arrondit rien. Absent (`undefined`), il n'est
 *   pas ecrit ; `null`, il se lit « — ».
 * - **L'arc de chaque segment est proportionnel a sa valeur**, rapportee a la
 *   somme des valeurs presentes — c'est la geometrie d'un anneau, pas un chiffre
 *   affiche. Ce qui s'ecrit dans la legende est `display` (« 41 % »), fourni
 *   par l'appelant, ou la valeur brute a defaut.
 * - une valeur `null` ne dessine aucun arc et se lit « — » ; un zero reel ne
 *   dessine rien mais se lit `0`.
 *
 * Aucune valeur positive : une phrase remplace l'anneau.
 */
export interface DonutSegment {
  readonly label: string;
  readonly value: Maybe;
  /** Texte de legende (« 41 % ») ; sinon la valeur brute mise en forme. */
  readonly display?: string;
  readonly token?: SeriesToken;
}

export interface DonutChartProps {
  readonly label: string;
  readonly segments: readonly DonutSegment[];
  /** Total ecrit au centre, tel que fourni. */
  readonly total?: DisplayableValue;
  /** Unite du total (« saisies »), sous le chiffre. */
  readonly totalCaption?: string;
  /** Intitule de la premiere colonne de la table (« Nature »). */
  readonly categoryHeader?: string;
  readonly formatValue?: ValueFormatter;
  readonly size?: number;
  readonly summary?: string;
  readonly emptyMessage?: string;
}

/** Epaisseur de l'anneau, en part de son diametre (maquette : rayon interieur 0,68). */
const THICKNESS = 0.16;
/** Vide entre deux arcs, en pixels le long de l'anneau. */
const GAP = 2;

export const DonutChart = ({
  label,
  segments,
  total,
  totalCaption,
  categoryHeader = 'Catégorie',
  formatValue = defaultFormat,
  size = 132,
  summary,
  emptyMessage = 'Aucune valeur à répartir.',
}: DonutChartProps): ReactElement => {
  const tokens = useTokens();
  const resolved = segments.map((segment, rank) => ({ ...segment, token: segment.token ?? seriesTokenAt(rank) }));
  const sum = resolved.reduce((acc, segment) => acc + (isValue(segment.value) && segment.value > 0 ? segment.value : 0), 0);
  const stroke = size * THICKNESS;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const positive = resolved.flatMap((segment) =>
    isValue(segment.value) && segment.value > 0 ? [{ label: segment.label, token: segment.token, value: segment.value }] : [],
  );
  // Chaque arc commence ou le precedent finit : le depart est la somme des longueurs d'avant.
  const arcs = positive.map((segment, rank) => {
    const length = (segment.value / sum) * circumference;
    const start = positive.slice(0, rank).reduce((acc, before) => acc + (before.value / sum) * circumference, 0);

    return { label: segment.label, token: segment.token, start, drawn: positive.length > 1 ? Math.max(length - GAP, 1) : length };
  });

  const shown = (segment: DonutSegment): string => segment.display ?? formatMaybe(segment.value, formatValue);
  const totalText = total === undefined ? null : displayValue(total);

  return (
    <ChartFrame
      label={label}
      summary={
        summary ??
        `${resolved.map((segment) => `${segment.label} ${shown(segment)}`).join(', ')}${totalText === null ? '' : `. Total ${totalText}${totalCaption === undefined ? '' : ` ${totalCaption}`}`}.`
      }
      table={{
        categoryHeader,
        columns: ['Valeur'],
        rows: resolved.map((segment) => ({ category: segment.label, cells: [shown(segment)] })),
      }}
      {...(sum > 0 ? {} : { empty: emptyMessage })}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px 24px' }}>
        <svg width={size} height={size} focusable="false" style={{ display: 'block', flex: 'none' }}>
          <g transform={`rotate(-90 ${String(size / 2)} ${String(size / 2)})`}>
            {arcs.map((arc, rank) => (
              <circle
                key={rank}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={tokens[arc.token]}
                strokeWidth={stroke}
                strokeDasharray={`${String(arc.drawn)} ${String(circumference - arc.drawn)}`}
                strokeDashoffset={-arc.start}
              />
            ))}
          </g>
          {totalText === null ? null : (
            <g textAnchor="middle" fill={tokens.textStrong} style={{ fontFamily: 'inherit' }}>
              <text x={size / 2} y={totalCaption === undefined ? size / 2 : size / 2 - 2} fontSize={24} fontWeight={800} dominantBaseline="central">
                {totalText}
              </text>
              {totalCaption === undefined ? null : (
                <text x={size / 2} y={size / 2 + 18} fontSize={12} fill={tokens.textMuted}>
                  {totalCaption}
                </text>
              )}
            </g>
          )}
        </svg>
        <Box sx={{ flex: '1 1 150px', minWidth: 0 }}>
          <Legend
            direction="column"
            items={resolved.map((segment) => ({
              label: segment.label,
              token: segment.token,
              value: shown(segment),
              shape: 'square',
            }))}
          />
        </Box>
      </Box>
    </ChartFrame>
  );
};
